// User Settings Page
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { NotificationSettings } from '../components/notifications';
import { CountryCodeSelect } from '../components/ui/CountryCodeSelect';
import { getUserRole } from '../utils/roles';
import { useAutoSubscribe } from '../hooks/usePushNotifications';
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useNotifications } from '../components/notifications';
import { calendarAPI, calendarIntegrationAPI } from '../services/api';

/**
 * @typedef {Object} User
 * @property {number} id
 * @property {string} email
 * @property {string} first_name
 * @property {string} last_name
 * @property {string=} phone_number
 * @property {string=} country_code
 * @property {'en'|'nl'|'de'|'fr'|'es'=} language
 * @property {'none'|'port'|'starboard'=} preferred_side
 * @property {boolean=} is_admin
 */

/**
 * Subset used by the profile form in this page
 * @typedef {Pick<User, 'email' | 'first_name' | 'last_name' | 'phone_number' | 'country_code' | 'language' | 'preferred_side'>} UserForm
 */

export default function Settings() {
  const { user, updateProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    profile: true,
    notifications: false,
    calendar: false,
    externalCalendar: false,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Calendar integration state
  const [expandedProvider, setExpandedProvider] = useState('google'); // Show Google Calendar by default
  const [calendarUuid, setCalendarUuid] = useState(null);
  const [isLoadingCalendar, setIsLoadingCalendar] = useState(true);
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const { showToast } = useNotifications();

  // Calendar Integration System state (for importing external calendars)
  const [integrations, setIntegrations] = useState([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);
  const [showAddIntegrationForm, setShowAddIntegrationForm] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [testingIntegration, setTestingIntegration] = useState(null);
  const [togglingIntegration, setTogglingIntegration] = useState(null);
  const [expandedInstructions, setExpandedInstructions] = useState({
    google: false,
    icloud: false,
    outlook: false,
    university: false,
  });
  const [integrationFormData, setIntegrationFormData] = useState({
    name: '',
    ical_url: '',
    is_active: true,
    sync_interval_minutes: 30,
  });

  // Removed prompts management state

  // Form state
  /** @type {[UserForm, React.Dispatch<React.SetStateAction<UserForm>>]} */
  const [formData, setFormData] = useState({
    email: user?.email || '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone_number: user?.phone_number || '',
    country_code: user?.country_code || '+31',
    language: user?.language || 'en',
    preferred_side: user?.preferred_side || 'none',
    is_advanced_user: user?.is_advanced_user || false,
  });

  // Auto-subscribe to push notifications when permission is granted
  const hasAutoSubscribed = useAutoSubscribe(true, 1000);

  // Load calendar UUID on component mount
  useEffect(() => {
    const loadCalendarUuid = async () => {
      try {
        setIsLoadingCalendar(true);
        const response = await calendarAPI.getCalendarUuid();
        setCalendarUuid(response.calendar_uuid);
      } catch (error) {
        console.error('Failed to load calendar UUID:', error);
        showToast('Failed to load calendar settings', 'error');
      } finally {
        setIsLoadingCalendar(false);
      }
    };

    loadCalendarUuid();
  }, [showToast]);

  // Load calendar integrations on component mount
  useEffect(() => {
    loadIntegrations();
  }, []);

  // Calendar providers with their details and instructions
  const calendarProviders = [
    {
      id: 'google',
      name: 'Google Calendar',
      description: 'Sync with Google Calendar',
      icon: '📅',
      color: 'bg-blue-50 border-blue-200',
      textColor: 'text-blue-700',
      url: '/api/calendar/google/ics',
      instructions: [
        {
          step: 1,
          title: 'Open Google Calendar',
          description: 'Go to calendar.google.com and sign in to your Google account',
        },
        {
          step: 2,
          title: 'Add Calendar',
          description: 'Click the "+" next to "Other calendars" in the left sidebar',
        },
        {
          step: 3,
          title: 'Subscribe to Calendar',
          description: 'Select "From URL" from the dropdown menu',
        },
        {
          step: 4,
          title: 'Paste the URL',
          description: 'Copy the URL below and paste it into the "URL of calendar" field',
        },
        {
          step: 5,
          title: 'Add Calendar',
          description: 'Click "Add calendar" to complete the setup',
        },
      ],
    },
    {
      id: 'apple',
      name: 'Apple Calendar',
      description: 'Sync with Apple Calendar',
      icon: '🍎',
      color: 'bg-gray-50 border-gray-200',
      textColor: 'text-gray-700',
      url: '/api/calendar/apple/ics',
      instructions: [
        {
          step: 1,
          title: 'Open Calendar App',
          description: 'Open the Calendar app on your Mac, iPhone, or iPad',
        },
        {
          step: 2,
          title: 'Add Calendar',
          description:
            'Go to File > New Calendar Subscription (Mac) or tap the "+" and "Add Calendar" (iOS)',
        },
        {
          step: 3,
          title: 'Enter URL',
          description: 'Paste the calendar URL below into the "Calendar URL" field',
        },
        {
          step: 4,
          title: 'Configure Settings',
          description: 'Choose your preferred settings and click "OK" or "Add"',
        },
      ],
    },
    {
      id: 'outlook',
      name: 'Microsoft Outlook',
      description: 'Sync with Outlook',
      icon: '📧',
      color: 'bg-purple-50 border-purple-200',
      textColor: 'text-purple-700',
      url: '/api/calendar/caldav/ics',
      instructions: [
        {
          step: 1,
          title: 'Open Outlook',
          description: 'Open Outlook on your computer or go to outlook.com',
        },
        {
          step: 2,
          title: 'Add Calendar',
          description: 'Go to Calendar view and click "Add calendar" > "Subscribe from web"',
        },
        {
          step: 3,
          title: 'Enter URL',
          description: 'Paste the calendar URL below and click "Import"',
        },
        {
          step: 4,
          title: 'Confirm',
          description: 'The calendar will appear in your calendar list',
        },
      ],
    },
    {
      id: 'ics',
      name: 'Other Calendar Apps',
      description: 'Use with any ICS-compatible app',
      icon: '📋',
      color: 'bg-green-50 border-green-200',
      textColor: 'text-green-700',
      url: '/api/calendar/ics',
      instructions: [
        {
          step: 1,
          title: 'Find Calendar Settings',
          description:
            'Open your calendar app and look for "Add Calendar" or "Subscribe to Calendar"',
        },
        {
          step: 2,
          title: 'Add by URL',
          description: 'Look for an option to add calendar by URL or web address',
        },
        {
          step: 3,
          title: 'Paste URL',
          description: 'Copy and paste the URL below into the calendar URL field',
        },
        {
          step: 4,
          title: 'Save',
          description: 'Save or confirm the calendar subscription',
        },
      ],
    },
  ];

  const getBaseUrl = () => {
    return window.location.origin;
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast('Calendar URL copied to clipboard!', 'success');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      showToast('Failed to copy URL to clipboard', 'error');
    }
  };

  const openCalendarUrl = (url) => {
    if (!calendarUuid) return;
    const fullUrl = `${getBaseUrl()}${url}/${calendarUuid}`;
    window.open(fullUrl, '_blank');
  };

  const toggleProvider = (providerId) => {
    setExpandedProvider(expandedProvider === providerId ? null : providerId);
  };

  const handleRefreshUuid = () => {
    setShowRefreshModal(true);
  };

  const confirmRefreshUuid = async () => {
    try {
      setIsLoadingCalendar(true);
      const response = await calendarAPI.refreshCalendarUuid();
      setCalendarUuid(response.calendar_uuid);
      showToast('Calendar URL refreshed successfully', 'success');
    } catch (error) {
      console.error('Failed to refresh calendar UUID:', error);
      showToast('Failed to refresh calendar URL', 'error');
    } finally {
      setIsLoadingCalendar(false);
      setShowRefreshModal(false);
    }
  };

  const cancelRefreshUuid = () => {
    setShowRefreshModal(false);
  };

  const getCalendarUrl = (provider) => {
    if (!calendarUuid) return '';
    return `${getBaseUrl()}${provider.url}/${calendarUuid}`;
  };

  // Calendar Integration System functions
  const loadIntegrations = async () => {
    try {
      setLoadingIntegrations(true);
      const data = await calendarIntegrationAPI.getIntegrations();
      setIntegrations(data);
    } catch (error) {
      console.error('Failed to load calendar integrations:', error);
      showToast('Failed to load calendar integrations', 'error');
    } finally {
      setLoadingIntegrations(false);
    }
  };

  const handleIntegrationSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoadingIntegrations(true);

      if (editingIntegration) {
        await calendarIntegrationAPI.updateIntegration(editingIntegration.id, integrationFormData);
        showToast('Calendar integration updated successfully', 'success');
      } else {
        await calendarIntegrationAPI.createIntegration(integrationFormData);
        showToast('Calendar integration added successfully', 'success');
      }

      // Reset form and reload
      setIntegrationFormData({
        name: '',
        ical_url: '',
        is_active: true,
        sync_interval_minutes: 30,
      });
      setShowAddIntegrationForm(false);
      setEditingIntegration(null);
      await loadIntegrations();
    } catch (error) {
      console.error('Failed to save calendar integration:', error);
      showToast('Failed to save calendar integration', 'error');
    } finally {
      setLoadingIntegrations(false);
    }
  };

  const handleEditIntegration = (integration) => {
    setEditingIntegration(integration);
    setIntegrationFormData({
      name: integration.name,
      ical_url: integration.ical_url,
      is_active: integration.is_active,
      sync_interval_minutes: integration.sync_interval_minutes,
    });
    setShowAddIntegrationForm(true);
  };

  const handleDeleteIntegration = async (integration) => {
    if (window.confirm(`Are you sure you want to delete "${integration.name}"?`)) {
      try {
        setLoadingIntegrations(true);
        await calendarIntegrationAPI.deleteIntegration(integration.id);
        showToast('Calendar integration deleted successfully', 'success');
        await loadIntegrations();
      } catch (error) {
        console.error('Failed to delete calendar integration:', error);
        showToast('Failed to delete calendar integration', 'error');
      } finally {
        setLoadingIntegrations(false);
      }
    }
  };

  const handleTestIntegration = async (integration) => {
    try {
      setTestingIntegration(integration.id);
      const result = await calendarIntegrationAPI.testIntegration(integration.id);
      showToast(`Test successful! Found ${result.event_count || 0} events.`, 'success');
    } catch (error) {
      console.error('Failed to test calendar integration:', error);
      showToast('Failed to test calendar integration', 'error');
    } finally {
      setTestingIntegration(null);
    }
  };

  const handleToggleIntegration = async (integration) => {
    try {
      setTogglingIntegration(integration.id);
      const updatedData = { ...integration, is_active: !integration.is_active };
      await calendarIntegrationAPI.updateIntegration(integration.id, updatedData);
      showToast(
        `Calendar integration ${updatedData.is_active ? 'enabled' : 'disabled'} successfully`,
        'success'
      );
      await loadIntegrations();
    } catch (error) {
      console.error('Failed to toggle calendar integration:', error);
      showToast('Failed to toggle calendar integration', 'error');
    } finally {
      setTogglingIntegration(null);
    }
  };

  const toggleInstructions = (provider) => {
    setExpandedInstructions((prev) => ({
      ...prev,
      [provider]: !prev[provider],
    }));
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Auto-strip leading zeros from phone number and validate length
    if (name === 'phone_number') {
      const cleanedValue = value.replace(/^0+/, '').replace(/\D/g, ''); // Remove leading zeros and non-digits
      setFormData((prev) => ({
        ...prev,
        [name]: cleanedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Validate phone number
  const validatePhoneNumber = (phone) => {
    if (!phone) return true; // Phone is optional
    const cleanPhone = phone.replace(/\D/g, ''); // Remove non-digits
    return cleanPhone.length >= 5;
  };

  // Validate email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    // Validate email format
    if (formData.email && !validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    // Validate phone number if provided
    if (formData.phone_number && !validatePhoneNumber(formData.phone_number)) {
      setError('Phone number must be at least 5 digits long');
      setIsLoading(false);
      return;
    }

    try {
      const result = await updateProfile(formData);
      if (result.success) {
        setSuccess('Profile updated successfully!');
        setIsEditing(false);
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || 'Failed to update profile');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle cancel editing
  const handleCancel = () => {
    setFormData({
      email: user?.email || '',
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      phone_number: user?.phone_number || '',
      country_code: user?.country_code || '+31',
      language: user?.language || 'en',
      preferred_side: user?.preferred_side || 'none',
      is_advanced_user: user?.is_advanced_user || false,
    });
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  // Removed prompts management logic

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage your account preferences and notification settings.
        </p>
      </div>

      <div className="space-y-6">
        {/* Success/Error Messages */}
        {success && (
          <div className="rounded-md border border-green-200 bg-green-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">{success}</p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Profile Information Section (Collapsible) */}
        <div className="rounded-lg bg-white p-6 shadow-md">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Profile Information</h2>
            <button
              onClick={() => toggleSection('profile')}
              className="flex items-center space-x-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <span>{expandedSections.profile ? 'Hide' : 'Show'}</span>
              {expandedSections.profile ? (
                <ChevronDownIcon className="h-4 w-4" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" />
              )}
            </button>
          </div>

          {expandedSections.profile && (
            <div className="mt-4">
              <div className="mb-4 flex items-center justify-end">
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Edit
                  </button>
                ) : (
                  <div className="flex space-x-2">
                    <button
                      onClick={handleCancel}
                      disabled={isLoading}
                      className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={isLoading}
                      className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                    >
                      {isLoading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                )}
              </div>

              {isEditing ? (
                <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                    {formData.email && !validateEmail(formData.email) && (
                      <p className="mt-1 text-xs text-red-600">
                        Please enter a valid email address
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="phone_number"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Phone Number
                    </label>
                    <div className="mt-1 flex">
                      <CountryCodeSelect
                        name="country_code"
                        value={formData.country_code}
                        onChange={handleInputChange}
                      />
                      <input
                        type="tel"
                        id="phone_number"
                        name="phone_number"
                        value={formData.phone_number}
                        onChange={handleInputChange}
                        placeholder="612345678"
                        className="block w-full rounded-l-none border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Phone number must be at least 5 digits. Leading zeros will be automatically
                      removed.
                    </p>
                    {formData.phone_number && !validatePhoneNumber(formData.phone_number) && (
                      <p className="mt-1 text-xs text-red-600">
                        Phone number must be at least 5 digits long
                      </p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                      Role (Read Only)
                    </label>
                    <input
                      type="text"
                      id="role"
                      value={getUserRole(user)}
                      disabled
                      className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 capitalize text-gray-500 shadow-sm sm:text-sm"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="preferred_side"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Preferred Side
                    </label>
                    <select
                      id="preferred_side"
                      name="preferred_side"
                      value={formData.preferred_side}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    >
                      <option value="none">No Preference</option>
                      <option value="port">Port</option>
                      <option value="starboard">Starboard</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="is_advanced_user" className="block text-sm font-medium text-gray-700">
                      Advanced user
                    </label>
                    <div className="mt-1 flex items-center">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={formData.is_advanced_user}
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            is_advanced_user: !prev.is_advanced_user,
                          }))
                        }
                        className={`${formData.is_advanced_user ? 'bg-blue-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      >
                        <span
                          className={`${formData.is_advanced_user ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                        />
                      </button>
                      <span className="ml-3 text-sm text-gray-700">
                        {formData.is_advanced_user ? 'On' : 'Off'}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Enables advanced features like locked seatings in Autoplanner.
                    </p>
                  </div>
                  <div>
                    <label htmlFor="language" className="block text-sm font-medium text-gray-700">
                      Language
                    </label>
                    <select
                      id="language"
                      name="language"
                      value={formData.language}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    >
                      <option value="en">🇬🇧 English</option>
                      <option value="nl">🇳🇱 Nederlands</option>
                      <option value="de">🇩🇪 Deutsch</option>
                      <option value="fr">🇫🇷 Français</option>
                      <option value="es">🇪🇸 Español</option>
                    </select>
                  </div>
                </form>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {user?.first_name} {user?.last_name}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="mt-1 text-sm text-gray-900">{user?.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Role</label>
                    <p className="mt-1 text-sm capitalize text-gray-900">{getUserRole(user)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {user?.country_code && user?.phone_number
                        ? `${user.country_code} ${user.phone_number}`
                        : user?.phone_number || 'Not provided'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Preferred Side
                    </label>
                    <p className="mt-1 text-sm capitalize text-gray-900">
                      {user?.preferred_side === 'none'
                        ? 'No Preference'
                        : user?.preferred_side || 'Not set'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Advanced user</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {user?.is_advanced_user ? 'On' : 'Off'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Language</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {user?.language === 'en'
                        ? '🇬🇧 English'
                        : user?.language === 'nl'
                          ? '🇳🇱 Nederlands'
                          : user?.language === 'de'
                            ? '🇩🇪 Deutsch'
                            : user?.language === 'fr'
                              ? '🇫🇷 Français'
                              : user?.language === 'es'
                                ? '🇪🇸 Español'
                                : user?.language || 'Not set'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Notification Settings (Collapsible) */}
        <div className="rounded-lg bg-white p-6 shadow-md">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Notification Settings</h2>
            <button
              onClick={() => toggleSection('notifications')}
              className="flex items-center space-x-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <span>{expandedSections.notifications ? 'Hide' : 'Show'}</span>
              {expandedSections.notifications ? (
                <ChevronDownIcon className="h-4 w-4" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" />
              )}
            </button>
          </div>
          {expandedSections.notifications && (
            <div className="mt-4">
              <NotificationSettings />
            </div>
          )}
        </div>

        {/* Calendar Integration Section (Collapsible) */}
        <div className="rounded-lg bg-white p-4 shadow-md sm:p-6">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-xl font-semibold">Calendar Integration</h2>
            <button
              onClick={() => toggleSection('calendar')}
              className="flex items-center space-x-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <span>{expandedSections.calendar ? 'Hide' : 'Show'}</span>
              {expandedSections.calendar ? (
                <ChevronDownIcon className="h-4 w-4" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" />
              )}
            </button>
          </div>
          {expandedSections.calendar && (
            <>
              {/* Header */}
              <div className="mb-6">
                <div className="mb-3 flex flex-col space-y-3 sm:flex-row sm:items-center sm:space-x-3 sm:space-y-0">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                    <CalendarDaysIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">
                      Connect your training sessions to your calendar
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  Your training sessions will automatically sync to your calendar and stay up to
                  date with any changes. The calendar includes sessions from 2 months ago to 1 year
                  ahead.
                </p>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">Calendar URL:</span>
                    {isLoadingCalendar ? (
                      <span className="text-xs text-gray-400">Loading...</span>
                    ) : calendarUuid ? (
                      <span className="font-mono text-xs text-gray-600">
                        {calendarUuid.substring(0, 8)}...
                      </span>
                    ) : (
                      <span className="text-xs text-red-500">Not available</span>
                    )}
                  </div>
                  <button
                    onClick={handleRefreshUuid}
                    disabled={isLoadingCalendar}
                    className="inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    Refresh URL
                  </button>
                </div>
              </div>

              {/* Calendar Providers */}
              {isLoadingCalendar ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-sm text-gray-500">Loading calendar settings...</div>
                </div>
              ) : !calendarUuid ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <div className="flex">
                    <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        Calendar URL Not Available
                      </h3>
                      <p className="text-sm text-red-700">
                        Unable to load your calendar URL. Please try refreshing the page or contact
                        support.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {calendarProviders.map((provider) => (
                    <div
                      key={provider.id}
                      className={`rounded-lg border p-4 ${provider.color} ${provider.textColor}`}
                    >
                      {/* Provider Header */}
                      <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl">{provider.icon}</span>
                          <div className="min-w-0 flex-1">
                            <h3 className="truncate font-semibold">{provider.name}</h3>
                            <p className="text-sm opacity-80">{provider.description}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => toggleProvider(provider.id)}
                          className="flex w-full items-center justify-center space-x-1 rounded-md border border-current px-3 py-2 text-sm font-medium hover:bg-white hover:bg-opacity-20 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:w-auto"
                        >
                          <span>
                            {expandedProvider === provider.id ? 'Hide' : 'View'} Instructions
                          </span>
                          {expandedProvider === provider.id ? (
                            <ChevronDownIcon className="h-4 w-4" />
                          ) : (
                            <ChevronRightIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>

                      {/* Expanded Content */}
                      {expandedProvider === provider.id && (
                        <div className="mt-4 space-y-4">
                          {/* Instructions */}
                          <div className="rounded-lg bg-white bg-opacity-50 p-4">
                            <h4 className="mb-3 font-medium">Step-by-Step Instructions</h4>
                            <div className="space-y-3">
                              {provider.instructions.map((instruction) => (
                                <div key={instruction.step} className="flex items-start space-x-3">
                                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-800 text-xs font-medium text-white shadow-sm">
                                    {instruction.step}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <h5 className="text-sm font-medium">{instruction.title}</h5>
                                    <p className="text-sm opacity-80">{instruction.description}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* URL Section */}
                          <div className="rounded-lg bg-white bg-opacity-50 p-4">
                            <h4 className="mb-3 font-medium">Calendar URL</h4>
                            <div className="space-y-3">
                              <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-x-2 sm:space-y-0">
                                <div className="flex-1 break-all rounded-md border bg-white px-3 py-2 font-mono text-sm text-gray-600">
                                  {getCalendarUrl(provider)}
                                </div>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => copyToClipboard(getCalendarUrl(provider))}
                                    className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:flex-none"
                                    title="Copy URL"
                                  >
                                    <ClipboardDocumentIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => openCalendarUrl(provider.url)}
                                    className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:flex-none"
                                    title="Open URL"
                                  >
                                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                              <button
                                onClick={() => openCalendarUrl(provider.url)}
                                className="w-full rounded-md border border-current px-4 py-2 text-sm font-medium hover:bg-white hover:bg-opacity-20 focus:outline-none focus:ring-2 focus:ring-offset-2"
                              >
                                Add to {provider.name}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Information Sections */}
              <div className="mt-6 space-y-4">
                {/* What's Included */}
                <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                  <div className="flex">
                    <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-green-800">
                        What's Included in Your Calendar
                      </h3>
                      <div className="mt-2 text-sm text-green-700">
                        <ul className="list-inside list-disc space-y-1">
                          <li>
                            All training sessions you're assigned to (as participant or coach)
                          </li>
                          <li>Session details including title, description, and location</li>
                          <li>Crew lineup information and your assigned role</li>
                          <li>Session times and duration</li>
                          <li>Automatic updates when sessions are modified</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Important Notes */}
                <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                  <div className="flex">
                    <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">Important Notes</h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <ul className="list-inside list-disc space-y-1">
                          <li>
                            Calendar updates may take a few minutes to appear in your calendar app
                          </li>
                          <li>Only sessions you're assigned to will appear in your calendar</li>
                          <li>Session changes will automatically sync to your calendar</li>
                          <li>This is a read-only feed - changes must be made in the app</li>
                          <li>Use "Refresh URL" to invalidate old calendar links if needed</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* External Calendar Integration Section (Collapsible) */}
        <div className="rounded-lg bg-white p-4 shadow-md sm:p-6">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-xl font-semibold">External Calendar Integration</h2>
            <button
              onClick={() => toggleSection('externalCalendar')}
              className="flex items-center space-x-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <span>{expandedSections.externalCalendar ? 'Hide' : 'Show'}</span>
              {expandedSections.externalCalendar ? (
                <ChevronDownIcon className="h-4 w-4" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" />
              )}
            </button>
          </div>
          {expandedSections.externalCalendar && (
            <>
              <div className="mb-6">
                <div className="mb-3 flex flex-col space-y-3 sm:flex-row sm:items-center sm:space-x-3 sm:space-y-0">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100">
                    <CalendarDaysIcon className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">
                      Import your external calendars to see events in the availability grid
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  Connect your external calendars (Google Calendar, Outlook, etc.) to see your
                  appointments and events in the availability grid. This helps you avoid scheduling
                  conflicts when planning training sessions.
                </p>
              </div>

              {/* Add Integration Button */}
              <div className="mb-6">
                <button
                  onClick={() => setShowAddIntegrationForm(true)}
                  className="inline-flex items-center rounded-md border border-transparent bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                >
                  <PlusIcon className="mr-2 h-4 w-4" />
                  Add Calendar Integration
                </button>
              </div>

              {/* Integrations List */}
              {loadingIntegrations ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-sm text-gray-500">Loading calendar integrations...</div>
                </div>
              ) : integrations.length === 0 ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center">
                  <CalendarDaysIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No calendar integrations
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Get started by adding your first calendar integration to see your external
                    events in the availability grid.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {integrations.map((integration) => (
                    <div
                      key={integration.id}
                      className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h3 className="text-sm font-medium text-gray-900">
                              {integration.name}
                            </h3>
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${integration.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                                }`}
                            >
                              {integration.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            Syncs every {integration.sync_interval_minutes} minutes
                          </p>
                          {editingIntegration?.id === integration.id && (
                            <p className="mt-1 break-all text-xs text-gray-400">
                              {integration.ical_url}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleTestIntegration(integration)}
                            disabled={testingIntegration === integration.id}
                            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                            title="Test the calendar connection and count events"
                          >
                            {testingIntegration === integration.id ? 'Testing...' : 'Test'}
                          </button>
                          <button
                            onClick={() => handleToggleIntegration(integration)}
                            disabled={togglingIntegration === integration.id}
                            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                            title={
                              integration.is_active
                                ? 'Temporarily disable this calendar'
                                : 'Enable this calendar'
                            }
                          >
                            {togglingIntegration === integration.id
                              ? 'Toggling...'
                              : integration.is_active
                                ? 'Disable'
                                : 'Enable'}
                          </button>
                          <button
                            onClick={() => handleEditIntegration(integration)}
                            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                            title="Edit calendar name, URL, or sync settings"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteIntegration(integration)}
                            className="inline-flex items-center rounded-md border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-700 shadow-sm hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500"
                            title="Permanently remove this calendar integration"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add/Edit Integration Form */}
              {showAddIntegrationForm && (
                <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h3 className="mb-4 text-lg font-medium text-gray-900">
                    {editingIntegration ? 'Edit Calendar Integration' : 'Add Calendar Integration'}
                  </h3>
                  <form onSubmit={handleIntegrationSubmit} className="space-y-4">
                    <div>
                      <label
                        htmlFor="integration-name"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Calendar Name
                      </label>
                      <input
                        type="text"
                        id="integration-name"
                        value={integrationFormData.name}
                        onChange={(e) =>
                          setIntegrationFormData({ ...integrationFormData, name: e.target.value })
                        }
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                        placeholder="e.g., Work Calendar, Personal Events, University Schedule"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Choose a descriptive name to easily identify this calendar in the
                        availability grid
                      </p>
                    </div>
                    <div>
                      <label
                        htmlFor="integration-url"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Calendar URL (iCal/ICS)
                      </label>
                      <input
                        type="url"
                        id="integration-url"
                        value={integrationFormData.ical_url}
                        onChange={(e) =>
                          setIntegrationFormData({
                            ...integrationFormData,
                            ical_url: e.target.value,
                          })
                        }
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                        placeholder="https://calendar.google.com/calendar/ical/..."
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        The public URL of your calendar in iCal format. See instructions below for
                        how to find this.
                      </p>
                    </div>
                    <div>
                      <label
                        htmlFor="integration-sync"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Sync Interval
                      </label>
                      <select
                        id="integration-sync"
                        value={integrationFormData.sync_interval_minutes}
                        onChange={(e) =>
                          setIntegrationFormData({
                            ...integrationFormData,
                            sync_interval_minutes: parseInt(e.target.value),
                          })
                        }
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                      >
                        <option value={15}>Every 15 minutes</option>
                        <option value={30}>Every 30 minutes</option>
                        <option value={60}>Every hour</option>
                        <option value={120}>Every 2 hours</option>
                        <option value={240}>Every 4 hours</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        How often the app checks for new events. More frequent updates use more data
                        but provide real-time information.
                      </p>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="integration-active"
                        type="checkbox"
                        checked={integrationFormData.is_active}
                        onChange={(e) =>
                          setIntegrationFormData({
                            ...integrationFormData,
                            is_active: e.target.checked,
                          })
                        }
                        className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <label
                        htmlFor="integration-active"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        Active
                      </label>
                    </div>
                    <p className="text-xs text-gray-500">
                      When active, this calendar's events will appear in the availability grid to
                      help avoid scheduling conflicts.
                    </p>
                    <div className="flex space-x-3">
                      <button
                        type="submit"
                        disabled={loadingIntegrations}
                        className="inline-flex items-center rounded-md border border-transparent bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50"
                      >
                        {loadingIntegrations
                          ? 'Saving...'
                          : editingIntegration
                            ? 'Update Integration'
                            : 'Add Integration'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddIntegrationForm(false);
                          setEditingIntegration(null);
                          setIntegrationFormData({
                            name: '',
                            ical_url: '',
                            is_active: true,
                            sync_interval_minutes: 30,
                          });
                        }}
                        className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Instructions */}
              <div className="mt-6 space-y-4">
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <div className="flex">
                    <InformationCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">
                        How to get your calendar URL
                      </h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <p className="mb-3">
                          You need the public URL of your calendar in iCal format. Here's how to
                          find it for different calendar services:
                        </p>
                        <ul className="list-inside list-disc space-y-2">
                          <li>
                            <strong>University Calendar:</strong>
                            <ol className="ml-4 mt-1 list-inside list-decimal space-y-1">
                              <li>
                                Search for "how to import calendar into google calendar [UNIVERSITY
                                NAME]"
                              </li>
                              <li>Follow the instructions to get the iCal URL</li>
                              <li>Copy the URL and paste it into the "Calendar URL" field</li>
                              <li>Click "Add Integration"</li>
                            </ol>
                          </li>
                          <li>
                            <strong>Google Calendar:</strong>
                            <ol className="ml-4 mt-1 list-inside list-decimal space-y-1">
                              <li>
                                Go to{' '}
                                <a
                                  href="https://calendar.google.com"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="underline"
                                >
                                  calendar.google.com
                                </a>
                              </li>
                              <li>Click the gear icon (Settings) in the top right</li>
                              <li>Select "Settings" from the dropdown</li>
                              <li>
                                In the left sidebar, click on the calendar name you want to share
                              </li>
                              <li>Scroll down to "Integrate calendar" section</li>
                              <li>Copy the "Secret address in iCal format"</li>
                            </ol>
                          </li>
                          <li>
                            <strong>Microsoft Outlook:</strong>
                            <ol className="ml-4 mt-1 list-inside list-decimal space-y-1">
                              <li>
                                Go to{' '}
                                <a
                                  href="https://outlook.live.com"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="underline"
                                >
                                  outlook.live.com
                                </a>
                              </li>
                              <li>Click the gear icon (Settings) in the top right</li>
                              <li>Select "View all Outlook settings"</li>
                              <li>Go to "Calendar" → "Shared calendars"</li>
                              <li>Click "Publish calendar"</li>
                              <li>Select the calendar and choose "Can view all details"</li>
                              <li>Copy the ICS link provided</li>
                            </ol>
                          </li>
                          <li>
                            <strong>Apple Calendar (Mac):</strong>
                            <ol className="ml-4 mt-1 list-inside list-decimal space-y-1">
                              <li>Open the Calendar app on your Mac</li>
                              <li>Right-click on the calendar name in the left sidebar</li>
                              <li>Select "Get Info"</li>
                              <li>Click "Publish calendar"</li>
                              <li>Choose your sharing options and click "Publish"</li>
                              <li>Copy the calendar URL provided</li>
                            </ol>
                          </li>
                          <li>
                            <strong>Apple Calendar (iOS):</strong>
                            <ol className="ml-4 mt-1 list-inside list-decimal space-y-1">
                              <li>Open the Calendar app on your iPhone/iPad</li>
                              <li>Tap "Calendars" at the bottom</li>
                              <li>Tap the "i" icon next to the calendar name</li>
                              <li>Scroll down and tap "Share Calendar"</li>
                              <li>Choose "Public Calendar" and copy the URL</li>
                            </ol>
                          </li>
                        </ul>
                        <p className="mt-3 text-xs">
                          <strong>Note:</strong> Make sure your calendar is set to "Public" or "Can
                          view all details" so the app can access your events.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Autoplanner Prompts Section removed */}
      </div>

      {/* Refresh UUID Confirmation Modal */}
      {showRefreshModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 max-w-md rounded-lg bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center space-x-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />
              <h3 className="text-lg font-medium text-gray-900">Refresh Calendar URL</h3>
            </div>

            <div className="mb-6">
              <p className="mb-3 text-sm text-gray-600">
                Are you sure you want to refresh your calendar URL? This action will:
              </p>
              <ul className="list-inside list-disc space-y-1 text-sm text-gray-600">
                <li>Generate a new unique URL for your calendar</li>
                <li>Invalidate all existing calendar subscriptions</li>
                <li>Require you to re-add the calendar to all your devices/apps</li>
              </ul>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={cancelRefreshUuid}
                disabled={isLoadingCalendar}
                className="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmRefreshUuid}
                disabled={isLoadingCalendar}
                className="flex-1 rounded-md border border-transparent bg-yellow-600 px-4 py-2 text-sm font-medium text-white hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-50"
              >
                {isLoadingCalendar ? 'Refreshing...' : 'Refresh URL'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
